import { ConfirmButtonArray } from '@td-client-frontend/common';
import { Form, Formik, FormikHelpers } from 'formik';
import React from 'react';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { Permissions } from '../../@types/Permissions';
import { Button } from '../../components/Button';
import { ModalFooterButtonGroup } from '../../components/Modal/NewModal';
import WithPermission from '../../components/WithPermission';
import { useAxios } from '../../context/AxiosContext';
import { usePermissionsList } from '../../hooks/usePermissionsList';
import { RoleForm } from '../ManageRoles/components/RoleForm';
import { transformOutgoingData } from '../ManageRoles/utils';
import { useRole } from '../../hooks/useRoles';
import { trimmingFields } from '../../utils/normalizingUtils';

export default function ManageRole({ id, onRequestClose }: { id: string | null; onRequestClose: () => void }) {
  const queryCache = useQueryClient();
  const { authAxios } = useAxios();
  const isNewRoleFlow = id === null;
  const { data: permissions } = usePermissionsList();
  const { data: roleValue, isLoading } = useRole({ id });

  const manageRoleApi = async (role: any): Promise<{ data: { manageRole: any } }> => {
    return authAxios.put('roles', { roles: [transformOutgoingData(role)] });
  };

  const createRoleApi = async (role: any): Promise<{ data: { manageRole: any } }> => {
    return authAxios.post('roles', { roles: [transformOutgoingData(role)] });
  };

  const saveRole = async (values: any) => {
    const normalizedValues = trimmingFields(values);
    try {
      const { data } = isNewRoleFlow ? await createRoleApi(normalizedValues) : await manageRoleApi(normalizedValues);
      const { manageRole } = data;
      const { errors } = manageRole;

      if (errors.length) {
        const errorMessage = errors
          .map((error: any) => {
            try {
              return JSON.parse(error).message;
            } catch (e) {
              return error;
            }
          })
          .join('\n');

        return toast.error(errorMessage, { position: 'top-center' });
      }
      toast.success(`Successfully ${isNewRoleFlow ? 'created' : 'updated'} role`, { position: 'top-center' });
      queryCache.invalidateQueries(['roles']);
      queryCache.invalidateQueries(`fetch-role-${manageRole.roles[0].id}`);
      onRequestClose();
    } catch (e) {
      toast.error(`Failed to ${isNewRoleFlow ? 'create' : 'update'} role`, { position: 'top-center' });
    }
  };

  return (
    <Formik
      initialValues={
        {
          roleNm: '',
          permissions
        } as any
      }
      enableReinitialize
      validateOnBlur
      validationSchema={Yup.object({
        roleNm: Yup.string()
          .min(3, 'Role Name must be at least 3 characters')
          .required('Role Name is required')
          .max(150, 'Role Name must be max 150 characters')
      })}
      onSubmit={async (values, { setSubmitting }: FormikHelpers<any>) => {
        setSubmitting(true);
        await saveRole(values);
        setSubmitting(false);
      }}
    >
      {({ isSubmitting, isValid, values, handleSubmit, submitForm, setValues, errors }) => (
        <Form className="flex flex-col w-full h-full pb-12" onSubmit={handleSubmit}>
          <div className="h-full px-4 py-8">
            <RoleForm
              roleValue={roleValue}
              isLoading={isLoading}
              permissions={values.permissions}
              setValues={setValues}
              errors={errors}
            />
          </div>
          <ModalFooterButtonGroup>
            <Button title="Cancel" kind="secondary" onClick={onRequestClose} />
            {(isNewRoleFlow || roleValue?.active) && (
              <WithPermission requiredPerms={[isNewRoleFlow ? Permissions.RoleCreate : Permissions.RoleWrite]}>
                <ConfirmButtonArray
                  id="modal_save_confirm"
                  onClick={submitForm}
                  tooltipOverrides={{
                    place: 'top',
                    arrowColor: '#5b2f91',
                    offset: {},
                    isModalConfirm: true
                  }}
                  className="btn btn-primary"
                  customization={{
                    tooltipText: isNewRoleFlow
                      ? 'Are you sure you want to create new role?'
                      : 'Are you sure you wish to save your changes?',
                    actionButtonLabel: 'Yes',
                    actionCancelButtonLabel: 'No'
                  }}
                  disabled={isSubmitting || !isValid}
                >
                  Save
                </ConfirmButtonArray>
              </WithPermission>
            )}
          </ModalFooterButtonGroup>
        </Form>
      )}
    </Formik>
  );
}
